import { ChangeEvent, ReactNode, forwardRef, useId } from "react";
import styles from "./NumberInputWithUnit.module.css";
import { InfinityToggle } from "../InfinityToggle/InfinityToggle";
import type { HelperVariant } from "../Input/Input";

export interface NumberInputWithUnitProps {
  label?: string;
  hint?: string;
  hintRight?: ReactNode;
  /**
   * Unit suffix shown inside the input (e.g. "msgs", "chars", "/min").
   * Omit it for a bare count — the field keeps the same 140px box, states and
   * helper layout, just without the suffix.
   */
  unit?: string;
  /**
   * Offer the ∞ toggle inside the box, for a duration that may be told to
   * never expire. Present enables it; `fallback` is the number switching it
   * back OFF restores when the operator never typed one here.
   *
   * ON is `value === 0` — the field's own value, not a second piece of state —
   * and the box then goes read-only, reads «Never» and drops its unit: a
   * duration that does not exist has no seconds to name. `min` / `max` are
   * dropped with it, since there is no longer a number in the box for them to
   * bound.
   *
   * A field that offers this must be NAMED — `aria-label` or `label` — and hold
   * a numeric `value`. The name is what the toggle borrows, and it is the only
   * thing telling several identical ∞ buttons on one page apart.
   */
  never?: { fallback: number };
  value: number | string;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  modified?: boolean;
  error?: string;
  helperVariant?: HelperVariant;
  /**
   * Optional helper text rendered under the input (e.g. "= 1 hour").
   *
   * A node rather than a string, for the same reason Alert's title is one: a
   * helper often quotes a config key or the raw value found in the blob, and
   * those are written as `<code>` everywhere else on the page they appear.
   */
  helperText?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  "aria-label"?: string;
}

export const NumberInputWithUnit = forwardRef<
  HTMLInputElement,
  NumberInputWithUnitProps
>(function NumberInputWithUnit(
  {
    label,
    hint,
    hintRight,
    unit,
    never,
    value,
    onChange,
    min,
    max,
    step,
    modified,
    error,
    helperVariant = "default",
    helperText,
    disabled,
    readOnly,
    required,
    placeholder,
    id,
    name,
    className = "",
    "aria-label": ariaLabel,
  },
  ref,
) {
  const reactId = useId();
  const fid = id ?? reactId;

  const helper = error ?? helperText ?? hint;
  const helperVariantResolved: HelperVariant = error
    ? "error"
    : helperVariant;

  const helperClass =
    helperVariantResolved === "warn"
      ? styles.helperWarn
      : helperVariantResolved === "error"
        ? styles.helperError
        : helperVariantResolved === "ok"
          ? styles.helperOk
          : styles.helper;

  // "Never" is a state of the VALUE, so nothing else has to be told about it.
  const isNever = !!never && value === 0;

  // The paddings are exclusive — the box clears whatever is actually drawn
  // inside it. Saying «Never» takes the unit away, so that state clears the
  // toggle alone whatever `unit` says.
  const roomFor =
    isNever || (never && !unit)
      ? styles.roomForToggle
      : never
        ? styles.roomForUnitAndToggle
        : unit
          ? styles.withUnit
          : "";

  const inputClass = [
    styles.input,
    roomFor,
    error ? styles.error : "",
    !error && modified ? styles.modified : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    // Forward the parsed numeric value; if blank, send NaN so callers can
    // decide their own clamping/default behavior.
    const raw = e.target.value;
    if (raw === "") {
      onChange(NaN);
      return;
    }
    const n = Number(raw);
    onChange(Number.isNaN(n) ? NaN : n);
  }

  return (
    <div className={styles.wrap}>
      {(label || hintRight) && (
        <div className={hintRight ? styles.labelRow : undefined}>
          {label && (
            <label className={styles.label} htmlFor={fid}>
              {label}
            </label>
          )}
          {hintRight && <span className={styles.hintRight}>{hintRight}</span>}
        </div>
      )}
      <div className={`${styles.unitWrap} ${never ? styles.hasNever : ""}`}>
        <input
          ref={ref}
          id={fid}
          name={name}
          // A word is not a number. The box has to become a text one to say
          // «Never» at all — the honest spelling of the state, where a blank
          // box or a bare 0 is something the operator has to decode.
          type={isNever ? "text" : "number"}
          className={inputClass}
          value={isNever ? "Never" : Number.isNaN(value as number) ? "" : value}
          onChange={handleChange}
          min={isNever ? undefined : min}
          max={isNever ? undefined : max}
          step={isNever ? undefined : step}
          disabled={disabled}
          // Read-only rather than disabled: the box keeps announcing its label
          // and stays in the tab order, so a keyboard user reaches the toggle
          // beside it.
          readOnly={readOnly || isNever}
          required={required}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-invalid={!!error || undefined}
          aria-describedby={
            error ? `${fid}-err` : helper ? `${fid}-help` : undefined
          }
        />
        {unit && !isNever && (
          <span className={styles.unit} aria-hidden>
            {unit}
          </span>
        )}
        {never && (
          <InfinityToggle
            // Narrowed rather than cast. `value` is typed `number | string`,
            // and a string one has no "never" to be in: handing it over as a
            // number would be a lie the compiler stops checking. Undefined
            // reads as "no duration to go back to", which is the truth.
            value={typeof value === "number" ? value : undefined}
            // The field speaks in numbers only. `undefined` cannot arrive here
            // — `fallback` is always a number — and NaN is what this component
            // already sends for an emptied box, so the page reads it the way
            // it reads every other blank.
            onChange={(next) => onChange(next ?? NaN)}
            fallback={never.fallback}
            fieldLabel={ariaLabel ?? label ?? ""}
            // A field the operator may not edit does not offer a button that
            // edits it — the box's own two props decide for the toggle as well.
            disabled={disabled || readOnly}
          />
        )}
      </div>
      {error && (
        <div id={`${fid}-err`} className={styles.helperError}>
          {error}
        </div>
      )}
      {!error && helper && (
        <div id={`${fid}-help`} className={helperClass}>
          {helper}
        </div>
      )}
    </div>
  );
});
